import React, { useEffect, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import Skeleton from 'react-loading-skeleton'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Card, CardBody, Table } from 'reactstrap'
import platformInstagram from 'assets/icons/instagram-logo.svg'
import platformYoutube from 'assets/icons/youtube-logo.svg'
import userDummyImg from 'assets/images/users/user-dummy-img.jpg'
import { fetchAllCollaborationsThunk } from 'slices/collaborations/thunk'
import { AppDispatch, RootState } from 'store'
import { CollaborationItem } from 'types/collaboration'
import { generateUniqueKey, isDateMoreThanDaysAgo } from 'utils/helpers'
import './style.scss'
import { fetchApplications } from 'services/api/application'
import { fetchApplicationsThunk } from 'slices/applications/thunk'
import { Application } from 'types/application'

const platformIconSrc = (platform?: string) => {
    if (platform === 'ig' || platform === 'instagram') return platformInstagram
    if (platform === 'yt' || platform === 'youtube') return platformYoutube
    return null
}

const platformLabel = (platform?: string) => {
    if (platform === 'ig') return 'Instagram'
    if (platform === 'yt') return 'YouTube'
    return platform ?? '—'
}

const formatHandle = (handle?: string) => {
    if (!handle) return '—'
    return handle.startsWith('@') ? handle : `@${handle}`
}

const formatStatusLabel = (status?: string) => {
    if (!status) return 'Pending'
    const normalized = status.toLowerCase()
    if (normalized === 'pending') return 'Pending'
    if (normalized === 'accepted' || normalized === 'approve' || normalized === 'approved') {
        return 'Accepted'
    }
    if (normalized === 'rejected' || normalized === 'reject') return 'Rejected'
    if (normalized === 'complete' || normalized === 'completed') return 'Completed'
    return status.charAt(0).toUpperCase() + status.slice(1)
}

const statusModifier = (status?: string) => {
    const normalized = (status ?? 'pending').toLowerCase()
    if (normalized === 'accepted' || normalized === 'approve' || normalized === 'approved') {
        return 'accepted'
    }
    if (normalized === 'rejected' || normalized === 'reject') return 'rejected'
    if (normalized === 'complete' || normalized === 'completed') return 'complete'
    return 'pending'
}


const getApplicationStatus = (application: Application) =>
    application.status ?? application?.status ?? 'pending'

const canViewCollaboration = (status?: string) => {
    const normalized = (status ?? '').toLowerCase()
    return (
        normalized === 'accepted' ||
        normalized === 'approve' ||
        normalized === 'approved' ||
        normalized === 'ongoing'
    )
}

const AppliedApplications = () => {
    const dispatch = useDispatch<AppDispatch>()
    const { applications, loading, count: totalApplications } = useSelector((state: RootState) => state.applications)

    useEffect(() => {
        dispatch(fetchApplicationsThunk({ force: false }))
    }, [])

    const showSkeleton = loading && applications.length === 0

    return (
        <div className="applications-section">
            <div className="applications-table-card-header"
            >
                <p className="applications-table-card-header__subtitle">
                    Total Applied Applications: {totalApplications}
                </p>
            </div>
            <Card className="applications-table-card border-0 shadow-sm">
                <CardBody className="p-0">
                    <div
                        id="applied-applications-scroll"
                        className="applications-table-scroll"
                    >
                        <div className="table-responsive">
                            <Table className="align-middle table-hover applications-table mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th scope="col">Campaign</th>
                                        <th scope="col">Brand</th>
                                        <th scope="col">Platform</th>
                                        <th scope="col">Creator</th>
                                        <th scope="col">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {showSkeleton &&
                                        Array.from({ length: 8 }).map((_, index) => (
                                            <tr key={index}>
                                                <td colSpan={6} className="py-2">
                                                    <Skeleton
                                                        height={48}
                                                        className="rounded-2"
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    {!showSkeleton &&
                                        applications.map((application) => {
                                            const socialAccount =
                                                application?.social_account
                                            const avatar =
                                                socialAccount?.profile_picture_url?.split(
                                                    '='
                                                )[0] || userDummyImg
                                            const handle =
                                                socialAccount?.handle ?? '—'
                                            const platform =
                                                application.campaign?.platform ??
                                                socialAccount?.platform
                                            const platformImg =
                                                platformIconSrc(platform)
                                            const status = getApplicationStatus(application)

                                            return (
                                                <tr
                                                    key={`${application.campaign.uuid}-${application.social_account.uuid}`}
                                                >   
                                                    <td className="fw-medium">
                                                        <Link to={`/campaign/${application.campaign.uuid}`}>
                                                            {application.campaign?.name ?? '—'}
                                                        </Link>
                                                    </td>
                                                    <td className="text-muted">
                                                        {application.campaign?.brand?.name ??
                                                            '—'}
                                                    </td>
                                                    <td>
                                                        <span className="d-inline-flex align-items-center gap-2">
                                                            {platformImg ? (
                                                                <img
                                                                    src={platformImg}
                                                                    alt=""
                                                                    className="applications-table__platform-img"
                                                                />
                                                            ) : (
                                                                <i
                                                                    className="ri-global-line applications-table__platform-icon"
                                                                    aria-hidden
                                                                />
                                                            )}
                                                            <span>
                                                                {platformLabel(platform)}
                                                            </span>
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="d-inline-flex align-items-center gap-2">
                                                            <img
                                                                src={avatar}
                                                                alt=""
                                                                className="applications-table__avatar rounded-circle"
                                                                width={32}
                                                                height={32}
                                                            />
                                                            <span className="text-muted">
                                                                {formatHandle(handle)}
                                                            </span>
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span
                                                            className={`applications-table__status applications-table__status--${statusModifier(status)}`}
                                                        >
                                                            {formatStatusLabel(status)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    {!showSkeleton &&
                                        !loading &&
                                        applications.length === 0 && (
                                            <tr>
                                                <td
                                                    colSpan={6}
                                                    className="text-center text-muted py-5"
                                                >
                                                    No applied applications found.
                                                </td>
                                            </tr>
                                        )}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </CardBody>
            </Card>
        </div>
    )
}

export default AppliedApplications
